import { useMemo } from "react";
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Typography, Chip
} from "@mui/material";
import { useSelector } from "react-redux";
import { useGetAuditLogsByTypeQuery } from "../../store/services/tenancy.service";

// Human-readable text for the reason codes response-service records (BD-792).
const REASON_LABELS = {
    "open-course": "Already assigned - course still open",
    "completed-cooldown": "Completed within the resend cooldown",
    "sim-cooldown": "Simulation sent within the resend cooldown"
};

const parseDetails = (row) => {
    try {
        return JSON.parse(row.sourceDetails || "{}");
    } catch {
        return {};
    }
};

const SuppressedSendsLog = () => {
    const { tenancyId } = useSelector((state) => state.tenancy);

    const { data, isLoading, error } = useGetAuditLogsByTypeQuery(
        { tenancyId, auditType: "suppressedsends", limit: 200 },
        { skip: !tenancyId }
    );

    const rows = useMemo(() => (data?.items || []).map((row) => {
        const details = parseDetails(row);
        return {
            sentTo: row.sentTo,
            contentId: details.ContentId,
            contentType: details.ContentType,
            reason: details.Reason,
            dateTime: row.dateTime
        };
    }), [data]);

    if (isLoading) {
        return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Typography color="error" sx={{ p: 2 }}>Could not load suppressed sends.</Typography>;
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Course and simulation sends that were skipped because the recipient already had the content or was inside a resend cooldown.
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Content</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                    No suppressed sends yet.
                                </TableCell>
                            </TableRow>
                        ) : rows.map((r, idx) => (
                            <TableRow key={`${r.sentTo}-${r.contentId}-${idx}`} hover>
                                <TableCell>{r.sentTo}</TableCell>
                                <TableCell>{r.contentId}</TableCell>
                                <TableCell>
                                    <Chip size="small" label={r.contentType === "simulation" ? "Simulation" : "Course"} />
                                </TableCell>
                                <TableCell>{REASON_LABELS[r.reason] || r.reason || "-"}</TableCell>
                                <TableCell>{r.dateTime ? new Date(r.dateTime).toLocaleString() : "-"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SuppressedSendsLog;
